// D13: the offline banner from design/screens/today-mobile.html. Reads come from the cache; every write is disabled.
// D28: a suspended organisation's Owner signs in read-only (UC-IDENTITY-001 A5); the shell says so once and writes go quiet.
import { formatTime } from "../lib/format";
import { useOnline } from "../lib/online";
import { useSessionView } from "../session/store";
import { Icon } from "../ui/icons";

export function OfflineBanner() {
  const { online, since } = useOnline();
  if (online) return null;
  return (
    <div className="alert warn offline" role="status">
      <Icon name="warning" />
      <span><b>Offline{since ? ` since ${formatTime(since)}` : ""}</b> — showing cached data. New orders and payments are unavailable until reconnected.</span>
    </div>
  );
}

/** The read-only notice for a suspended organisation: every write is refused until the platform lifts the suspension (D28). */
export function SuspendedBanner() {
  const s = useSessionView();
  if (!s || s.organisation.status !== "Suspended") return null;
  return (
    <div className="alert warn suspended" role="status">
      <Icon name="warning" />
      <span><b>Read-only</b> — {s.organisation.tradingName ?? s.organisation.legalName} is suspended. Records can be read and printed; every change is refused until the suspension is lifted.</span>
    </div>
  );
}

export type WriteBlock = "offline" | "suspended";
export const WRITE_BLOCK_TITLE: Record<WriteBlock, string> = { offline: "Unavailable offline", suspended: "Read-only while the organisation is suspended" };

/** Why writes are off right now — offline (D13) or a suspended organisation (D28) — or null when they are allowed. */
export function useWriteBlock(): WriteBlock | null {
  const { online } = useOnline();
  const s = useSessionView();
  if (!online) return "offline";
  if (s?.organisation.status === "Suspended") return "suspended";
  return null;
}

/** Buttons that write wear this so they go quiet offline (D13) and for a suspended organisation (D28). */
export function useWritesEnabled(): boolean {
  return useWriteBlock() === null;
}
